<template>
  <Listbox v-model="selected_record_or_activity" as="div" class="relative w-full">
    <div class="relative h-full">
      <ListboxButton
        class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
        <span v-if="!selected_record_or_activity" class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{
          $t("Select Condition")
        }}</span>
        <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
          selected_record_or_activity
        }}</span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </span>
      </ListboxButton>

      <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <ListboxOptions
          class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
          <ListboxOption
            v-for="activity in selected_record_or_activity_options"
            :key="activity"
            v-slot="{ active, selected }"
            as="template"
            :value="activity">
            <li
              :class="[
                active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                'cursor-default relative py-2 pl-5 pr-4 text-left has-tooltip'
              ]">
              <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                {{ activity }}
              </span>

              <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                {{ activity }}
              </div>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>

  <AdvancedMetricsFilterForm
    v-if="selected_record_or_activity"
    :result-references="previousLabels"
    :record-filter="selected_record_or_activity == 'Record'"
    :activity-filter="selected_record_or_activity && selected_record_or_activity !== 'Record'"
    :activity-filter-selected="selected_record_or_activity"
    :initial-filter="condition"
    :object-type="null"
    @update="($event) => handleUpdateFilter($event)" />
</template>

<script>
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { ChevronDown } from "lucide-vue-next"

export default {
  components: { AdvancedMetricsFilterForm, Listbox, ListboxButton, ListboxOption, ListboxOptions, ChevronDown },
  props: { selectedNode: null, previousLabels: null },
  emits: ["updateValues"],

  data() {
    return {
      prevent_watch: false,
      condition: "",
      selected_record_or_activity: "",
      selected_record_or_activity_options: [this.$t("Record"), this.$t("Plan"), this.$t("Goal"), this.$t("Task"), this.$t("Interaction")]
    }
  },
  computed: {},
  watch: {
    condition: {
      deep: true,
      handler() {
        if (!this.prevent_watch) {
          this.handleUpdateValues()
        }
      }
    },
    selected_record_or_activity: {
      deep: true,
      handler() {
        if (!this.prevent_watch) {
          this.handleUpdateValues()
        }
      }
    }
  },

  created() {
    this.init()
  },

  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({ ...item, value: item.value == null ? "" : item.value.trim() })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.condition = parsed_filter.filter
    },

    handleUpdateValues() {
      let valid_calc = this.condition?.includes("id ==")

      const data = { condition: this.condition, selected_record_or_activity: this.selected_record_or_activity, valid: valid_calc }
      this.$emit("updateValues", data)
    },
    init() {
      this.prevent_watch = true
      this.condition = this.$utils.clone(this.selectedNode.data.values?.condition)
      if (this.selectedNode.data.values?.selected_record_or_activity) {
        this.selected_record_or_activity = this.selectedNode.data.values?.selected_record_or_activity
      }

      setTimeout(() => {
        this.prevent_watch = false
      }, 1000)
    }
  }
}
</script>
